import { lazy, MouseEvent, ReactNode, Suspense, SyntheticEvent, useCallback, useState } from 'react'
import { generatePath, Link as RouterLink } from 'react-router-dom'
import Box from '@mui/material/Box'
import Link from '@mui/material/Link'
import Tooltip from '@mui/material/Tooltip'
import {
  AD_HOC_TASK_STATUSES,
  ITask,
  TaskSource,
  TaskStatus,
  asTaskSource,
} from '@common/interfaces/task'
import cn from 'classnames'
import Icon, { IconOrSvgGlyph } from '../../../components/Common/Icon'
import IconButton from '../../../components/Common/IconButton'
import CustomFieldOptionPill from '../../../components/CustomFields/CustomFieldOptionPill'
import { ROUTES } from '../../../constants/routes'
import { getTaskCompanyOrOrgLink, getTaskNavigationLink, getTaskSourceTarget } from '../taskHelpers'
import { TASK_SOURCE_ICONS } from '../taskSourcePresentation'
import TaskStatusPill from './TaskStatusPill'
import styles from './taskCellRenderers.module.scss'

// Task rows render on every page through the right sidebar; the modal (and its markdown) loads on open.
const FeedbackSubmissionModal = lazy(
  () => import('../../MyFeedbackPage/components/FeedbackSubmissionModal'),
)

// The row itself is clickable (it opens the detail panel), so a source click must
// not also select the row. Module scope: it captures nothing.
const stopRowClick = (e: MouseEvent) => e.stopPropagation()
// React events bubble out of a portal to its owner, so the modal is fenced off from the row.
const stopRowEvent = (e: SyntheticEvent) => e.stopPropagation()

/** Opens the submission behind a Feedback task over the current page. */
const FeedbackSourceLink = ({
  feedbackId,
  label,
  className,
  children,
}: {
  feedbackId: string
  label: string
  className: string
  children: ReactNode
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const handleOpen = useCallback((e: MouseEvent) => {
    e.stopPropagation()
    setIsOpen(true)
  }, [])
  const handleClose = useCallback(() => setIsOpen(false), [])

  return (
    <>
      <Tooltip title={label}>
        <Link
          component="button"
          type="button"
          underline="none"
          className={className}
          aria-label={label}
          onClick={handleOpen}
          data-cy="task-source-feedback"
        >
          {children}
        </Link>
      </Tooltip>
      {isOpen && (
        <span
          onClick={stopRowEvent}
          onMouseDown={stopRowEvent}
          onPointerDown={stopRowEvent}
          onKeyDown={stopRowEvent}
        >
          <Suspense fallback={null}>
            <FeedbackSubmissionModal feedbackId={feedbackId} onClose={handleClose} />
          </Suspense>
        </span>
      )}
    </>
  )
}

/**
 * Where the task came from, as an icon that traces back to it. Icon-only in the
 * table (the column header carries the label), with the source name on hover.
 * `withLabel` renders icon + text for the detail panel, where there is no
 * header to lean on.
 *
 * Pass the `task` to make it traceable; `source` alone is for the inline
 * creation row, which has no task yet and is always Manual. The outcomes of
 * `getTaskSourceTarget` render as an in-app link, an external link to the call
 * recording, the feedback submission's modal, or (a task Arc was asked for in
 * chat) a tooltip with nothing to open. No target falls through to the plain, non-interactive glyph.
 */
export const TaskSourceIndicator = ({
  task,
  source: rawSource,
  withLabel = false,
}: {
  task?: ITask
  source?: string | null
  withLabel?: boolean
}) => {
  const source = asTaskSource(task?.source ?? rawSource) ?? TaskSource.Manual
  const target = task ? getTaskSourceTarget(task) : null

  const icon = (
    <span className={styles.sourceIcon} aria-hidden>
      <IconOrSvgGlyph
        icon={TASK_SOURCE_ICONS[source]}
        size="sm"
        svgClassName={styles.sourceGlyph}
      />
    </span>
  )
  const content = withLabel ? (
    <>
      {icon}
      <span>{source}</span>
    </>
  ) : (
    icon
  )

  const linkClassName = cn(styles.sourceLink, { [styles.sourceLinkWithLabel]: withLabel })

  if (target?.kind === 'internal') {
    return (
      <Tooltip title={target.label}>
        <Link
          component={RouterLink}
          to={target.path}
          underline="none"
          className={linkClassName}
          aria-label={target.label}
          onClick={stopRowClick}
        >
          {content}
        </Link>
      </Tooltip>
    )
  }

  if (target?.kind === 'feedback') {
    return (
      <FeedbackSourceLink
        feedbackId={target.feedbackId}
        label={target.label}
        className={linkClassName}
      >
        {content}
      </FeedbackSourceLink>
    )
  }

  if (target?.kind === 'external') {
    return (
      <Tooltip title={target.label}>
        <Link
          href={target.url}
          target="_blank"
          rel="noopener noreferrer"
          underline="none"
          className={linkClassName}
          aria-label={target.label}
          onClick={stopRowClick}
        >
          {content}
        </Link>
      </Tooltip>
    )
  }

  if (withLabel) {
    return (
      <Tooltip title={target?.label ?? ''}>
        <Box className={styles.sourceWithLabel}>{content}</Box>
      </Tooltip>
    )
  }

  // inline-flex so the box hugs the glyph and the tooltip anchors to the icon
  // rather than the full cell width. Placement comes from the cell's own
  // genericSs.tableTextLeft, same as every other column.
  return (
    <Tooltip title={target?.label ?? source}>
      <Box className={styles.sourceIconOnly} aria-label={source}>
        {icon}
      </Box>
    </Tooltip>
  )
}

/**
 * Renders the read-only "Company / Org" cell content (getTaskCompanyOrOrgLink): a
 * router link to the company's profile at its current stage, or to the
 * organization's page. Falls back to plain text for a name that resolves to no
 * page, and to "-" when there is nothing to show.
 */
export const TaskCompanyLink = ({ task }: { task: ITask }) => {
  const link = getTaskCompanyOrOrgLink(task)
  if (!link) {
    return <>-</>
  }
  if (!link.path) {
    return <>{link.name}</>
  }
  return (
    <Link
      component={RouterLink}
      to={link.path}
      underline="hover"
      onClick={(e) => e.stopPropagation()}
    >
      {link.name}
    </Link>
  )
}

/**
 * Small recurrence indicator shown on rows whose task was spawned by (or is the
 * seed of) a recurrence rule. Clicking opens the task's detail panel, where the
 * rule can be edited, paused, or deleted. Renders nothing for non-recurring tasks.
 */
export const TaskRecurrenceIndicator = ({ task, onOpen }: { task: ITask; onOpen?: () => void }) => {
  if (!task.recurrenceRuleId) {
    return null
  }
  return (
    <IconButton
      title="Recurring task"
      data-row-action
      size="small"
      className={styles.recurrenceButton}
      onClick={
        onOpen
          ? (e) => {
              e.stopPropagation()
              onOpen()
            }
          : undefined
      }
    >
      <Icon name="repeat" size="sm" />
    </IconButton>
  )
}

/**
 * Renders the read-only "Category" cell content as the custom field's coloured
 * option pill. Where the task has an upstream surface, the pill is also the
 * deep-link button into it (Funding, Reporting, Collections, …).
 */
export const TaskCategoryLink = ({
  task,
  onNavigateToActivity,
}: {
  task: ITask
  onNavigateToActivity: (task: ITask) => void
}) => {
  const option = task.categoryOption
  if (!option) {
    return <>-</>
  }

  // Same pill any single-select option renders as; the deep-link wraps it rather
  // than replacing it, so Category reads identically to a user-defined field.
  const pill = <CustomFieldOptionPill name={option.name} color={option.color} />

  if (!getTaskNavigationLink(task)) {
    return pill
  }
  return (
    <Link
      component="button"
      type="button"
      underline="none"
      className={styles.categoryLink}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        onNavigateToActivity(task)
      }}
    >
      {pill}
    </Link>
  )
}

// How many project pills a row shows before the rest collapse into a "+n".
// Two keeps the column at one line at its default width; a task in five
// projects would otherwise set the height of every row on the page.
const MAX_VISIBLE_PROJECT_PILLS = 2

/**
 * Renders the read-only "Projects" cell content: one pill per project the task
 * sits in, each linking to that project's board. Beyond the first two, the
 * remainder collapse into a "+n" pill that names them on hover.
 *
 * The pill is the same component a custom-field option renders as — a project's
 * colour comes from the same palette — so a project reads like every other
 * coloured token in the row rather than as a bespoke chip.
 */
export const TaskProjectsCell = ({ task }: { task: ITask }) => {
  const placements = (task.placements ?? []).filter((placement) => placement.project)
  if (placements.length === 0) {
    return <>-</>
  }

  const visible = placements.slice(0, MAX_VISIBLE_PROJECT_PILLS)
  const overflow = placements.slice(MAX_VISIBLE_PROJECT_PILLS)

  return (
    <Box className={styles.projectsCell}>
      {visible.map((placement) => (
        <Link
          key={placement.id}
          component={RouterLink}
          to={generatePath(ROUTES.TASK_PROJECT_PAGE, { id: placement.projectId })}
          underline="none"
          className={styles.projectPillLink}
          onClick={stopRowClick}
        >
          <CustomFieldOptionPill
            name={placement.project?.name ?? ''}
            color={placement.project?.color ?? ''}
          />
        </Link>
      ))}
      {overflow.length > 0 && (
        <Tooltip title={overflow.map((placement) => placement.project?.name).join(', ')}>
          <span className={styles.projectsOverflow}>+{overflow.length}</span>
        </Tooltip>
      )}
    </Box>
  )
}

/** Stable free function so SelectField's `renderItem` can reference it
 *  without each caller re-wrapping in useCallback. */
export const renderTaskStatusPill = (value: string) => <TaskStatusPill value={value} />

/** The statuses a user can set on an ad-hoc task — shared by the inline-create
 *  row, the table-row status select, and the detail-panel status select. */
export const AD_HOC_STATUS_OPTIONS = AD_HOC_TASK_STATUSES.map((status) => ({
  value: status,
  label: status,
}))

/** Every task-owned status, for the bulk bar's Set status. */
export const ALL_TASK_STATUS_OPTIONS = Object.values(TaskStatus).map((status) => ({
  value: status,
  label: status,
}))
